import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ArrowUpRight } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { apiService, UpcomingEvent } from '../services/api';
import { getEventPresentation } from '../lib/eventPresentation';

interface SearchDropdownProps { query: string; isOpen: boolean; onClose: () => void; embedded?: boolean; }
const normalize = (value: string) => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();

export function SearchDropdown({ query, isOpen, onClose, embedded = false }: SearchDropdownProps) {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || loaded) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    const timeout = window.setTimeout(() => {
      cancelled = true;
      setLoading(false);
      setError(true);
    }, 8000);
    apiService.getUpcomingEventsShared().then((response) => {
      if (cancelled) return;
      setEvents(response.events.filter((event) => event.eventUrl));
      setLoading(false);
      setLoaded(true);
    }).catch(() => { if (!cancelled) { setError(true); setLoading(false); } })
      .finally(() => window.clearTimeout(timeout));
    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [isOpen, loaded, attempt]);

  const filtered = useMemo(() => {
    const words = normalize(query).split(' ').filter(Boolean);
    if (!words.length) return events.slice(0, 6);
    return events.filter((event) => {
      const { name, venue } = getEventPresentation(event);
      const searchable = normalize(`${event.eventName} ${event.location || ''} ${name} ${venue}`);
      return words.every((word) => searchable.includes(word));
    });
  }, [query, events]);

  useEffect(() => {
    if (!isOpen || embedded) return;
    const outside = (event: MouseEvent) => {
      if (!dropdownRef.current?.parentElement?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [isOpen, onClose, embedded]);

  if (!isOpen) return null;
  return <div ref={dropdownRef} className={`unique-search-results${embedded ? ' unique-search-results--embedded' : ''}`}>
    <div className="unique-search-results__heading">
      <Search size={16} aria-hidden="true" />
      <span role="status">{loading ? 'Buscando eventos…' : query ? `${filtered.length} ${filtered.length === 1 ? 'resultado' : 'resultados'}` : 'Na agenda'}</span>
      {!embedded && <button aria-label="Fechar resultados da busca" onClick={onClose}><X size={16} /></button>}
    </div>
    <div className="unique-search-results__list">
      {loading ? <p className="unique-search-results__empty">Carregando a programação…</p>
        : error ? <div className="unique-search-results__empty"><p>Não foi possível carregar os eventos.</p><button className="unique-button" onClick={() => setAttempt((value) => value + 1)}>Tentar novamente</button></div>
        : !filtered.length ? <p className="unique-search-results__empty">Nenhum evento encontrado. Tente outro nome ou cidade.</p>
        : filtered.map((event) => {
          const { name, venue, date } = getEventPresentation(event);
          return <a key={event.eventId} href={event.eventUrl} target="_blank" rel="noopener" onClick={onClose} className="unique-search-result">
          <div className="unique-search-result__art"><ImageWithFallback src={event.thumbnailUrl || event.imageUrl} alt="" loading="lazy" className="unique-search-result__image" /></div>
          <div className="unique-search-result__copy">
            <span className="unique-search-result__title">{name}</span>
            {venue && <span>{venue}</span>}
            <span>{date}</span>
          </div>
          <ArrowUpRight size={16} aria-hidden="true" />
        </a>;
        })}
    </div>
  </div>;
}
